<template>
  <!-- 多选气泡：快捷批量操作（内容随高度滚动，标题吸顶） -->
  <div v-if="isMultiSelect" class="bubble-menu multi-select" :style="menuStyle" @click.stop @wheel.stop>
    <div class="bubble-title bubble-title--sticky">
      <span class="drag-handle" @mousedown.prevent="startDrag" title="拖拽移动">⋮⋮</span>
      <span class="obj-name">已选中 {{ selectedIds.length }} 个对象</span>
      <button class="action-btn pin-btn" :class="{ active: pinned }" @click="togglePin" :title="pinned ? '取消固定' : '固定位置'">
        <Pin :size="16" />
      </button>
    </div>

    <section class="bubble-section">
      <h4 class="bubble-section-title">标签</h4>
      <div class="bubble-follow-floor-row bubble-label-toggle-row">
        <label class="bubble-follow-floor-label">
          <input
            type="checkbox"
            :checked="batchTopLabelVisible"
            @change="doBatchTopLabel(($event.target as HTMLInputElement).checked)"
          />
          显示名称标签
        </label>
        <label class="bubble-follow-floor-label">
          <input
            type="checkbox"
            :checked="batchStatusLightVisible"
            @change="doBatchStatusLight(($event.target as HTMLInputElement).checked)"
          />
          显示状态标签
        </label>
        <label class="bubble-follow-floor-label">
          <input
            type="checkbox"
            :checked="batchInfoCardVisible"
            @change="doBatchInfoCard(($event.target as HTMLInputElement).checked)"
          />
          显示信息卡
        </label>
      </div>
    </section>

    <section class="bubble-section">
      <h4 class="bubble-section-title">对齐</h4>
      <div class="align-row">
        <button class="align-btn" @click="doAlign('left')" title="左对齐">⊢</button>
        <button class="align-btn" @click="doAlign('centerH')" title="水平居中">⊥</button>
        <button class="align-btn" @click="doAlign('right')" title="右对齐">⊣</button>
        <button class="align-btn" @click="doAlign('top')" title="前对齐">⊤</button>
        <button class="align-btn" @click="doAlign('centerV')" title="前后居中">⊥</button>
        <button class="align-btn" @click="doAlign('bottom')" title="后对齐">⊥</button>
      </div>
      <div class="align-row">
        <button class="align-btn" @click="doAlign('distributeH')" title="水平等距分布">水平等距</button>
        <button class="align-btn" @click="doAlign('distributeV')" title="前后等距分布">垂直等距</button>
        <button class="align-btn" @click="doAlign('sameWidth')" title="统一宽度">同宽</button>
        <button class="align-btn" @click="doAlign('sameDepth')" title="统一进深">同深</button>
      </div>
    </section>

    <section class="bubble-section">
      <h4 class="bubble-section-title">编辑</h4>
      <div class="batch-ops">
        <button type="button" class="align-btn" @click="emit('open-array')">阵列</button>
        <button type="button" class="align-btn" @click="doBatchPrefixName">命名前缀</button>
        <button type="button" class="align-btn" @click="doGroup">成组</button>
        <button type="button" class="align-btn" :disabled="!selectionHasGroup" @click="doUngroup">取消组合</button>
        <button type="button" class="align-btn" @click="doBatchLock(true)">全部锁定</button>
        <button type="button" class="align-btn" @click="doBatchLock(false)">全部解锁</button>
        <button type="button" class="align-btn" @click="doBatchInfoCardLine">信息卡模板</button>
      </div>
      <div class="bubble-actions bubble-actions--color">
        <span class="multi-color-label">统一颜色</span>
        <input v-model="batchColorHex" type="color" class="multi-color-input" />
        <button type="button" class="action-btn action-btn-text" @click="applyBatchUniformColor">应用</button>
      </div>
    </section>

    <section v-if="bulkSameType" class="bubble-section">
      <h4 class="bubble-section-title">变换（同类型）</h4>
      <button
        type="button"
        class="align-btn"
        :class="{ active: showBulkTransform }"
        @click="showBulkTransform = !showBulkTransform"
      >
        {{ showBulkTransform ? '收起尺寸' : '批量改尺寸 / 旋转' }}
      </button>
      <div v-show="showBulkTransform" class="sub-panel params-panel multi-size-panel">
        <SizePanel :object-ids="selectedIds" :on-history-push="props.onHistoryPush" />
      </div>
    </section>

    <section class="bubble-section">
      <button type="button" class="action-btn danger" @click="doDeleteAll">
        <Trash2 :size="14" /> 全部删除
      </button>
    </section>
    <div class="resize-handle" @mousedown.prevent="startResize" title="拖拽缩放" />
  </div>

  <!-- 单选气泡菜单 -->
  <div v-else-if="props.id" class="bubble-menu bubble-menu--single" :style="menuStyleSingle" @click.stop>
    <!-- 顶部固定：标题 + 操作栏 -->
    <div class="bubble-menu-head">
    <!-- 名称（可内联编辑） -->
    <div class="bubble-title">
      <span class="drag-handle" @mousedown.prevent="startDrag" title="拖拽移动">⋮⋮</span>
      <input
        v-if="editingName"
        ref="nameInputRef"
        v-model="nameVal"
        class="name-input"
        @blur="saveName"
        @keyup.enter="saveName"
        @keyup.escape="editingName = false"
      />
      <span v-else class="obj-name" title="双击编辑名称" @dblclick="startEditName">{{ objDisplayName }}</span>
      <button class="action-btn pin-btn" :class="{ active: pinned }" @click="togglePin" :title="pinned ? '取消固定' : '固定位置'">
        <Pin :size="16" />
      </button>
    </div>
    <div class="bubble-divider" />
    <div v-if="obj && positionDisplay" class="bubble-coord-row bubble-coord-row--edit">
      <span class="coord-label">X</span>
      <input
        class="coord-input"
        type="number"
        step="0.5"
        :value="coordX"
        @change="onCoordChange('x', ($event.target as HTMLInputElement).value)"
      />
      <span class="coord-label">Z</span>
      <input
        class="coord-input"
        type="number"
        step="0.5"
        :value="coordZ"
        @change="onCoordChange('z', ($event.target as HTMLInputElement).value)"
      />
      <span class="coord-label">Y</span>
      <input
        class="coord-input"
        type="number"
        step="0.1"
        :value="coordY"
        @change="onCoordChange('y', ($event.target as HTMLInputElement).value)"
      />
    </div>
    <div v-if="obj && positionDisplay" class="bubble-follow-floor-row bubble-single-toggle-row">
      <button type="button" class="action-btn action-btn-text" @click="snapToFloor" title="把物体底部放到当前楼面">
        贴回楼面
      </button>
      <button
        type="button"
        class="action-btn action-btn-text"
        :class="{ active: topLabelVisible }"
        @click="toggleTopLabel"
        :title="topLabelVisible ? '隐藏名称标签' : '显示名称标签'"
      >
        显示名称
      </button>
      <button
        type="button"
        class="action-btn action-btn-text"
        :class="{ active: statusLightVisible }"
        @click="toggleStatusLight"
        :title="statusLightVisible ? '关闭状态标签' : '显示状态标签'"
      >
        显示状态
      </button>
      <button
        type="button"
        class="action-btn action-btn-text"
        :class="{ active: infoCardVisible }"
        @click="toggleInfoCard"
        :title="infoCardVisible ? '隐藏信息卡' : '显示信息卡'"
      >
        显示信息卡
      </button>
    </div>

    <div class="bubble-actions bubble-actions--grouped">
      <span class="action-group action-group--icons">
        <button type="button" class="action-btn" @click="doDuplicate" title="复制">
          <Copy :size="14" />
        </button>
        <button type="button" class="action-btn" @click="emit('open-array')" title="阵列复制">
          <Grid2x2 :size="14" />
        </button>
        <button type="button" class="action-btn" @click="toggleLock" :class="{ active: isLocked }" :title="isLocked ? '解锁' : '锁定'">
          <Lock v-if="isLocked" :size="14" />
          <Unlock v-else :size="14" />
        </button>
        <button type="button" class="action-btn" @click="toggleHidden" :class="{ active: isHidden }" :title="isHidden ? '显示' : '隐藏'">
          <EyeOff v-if="!isHidden" :size="14" />
          <Eye v-else :size="14" />
        </button>
        <button type="button" class="action-btn danger" @click="doDelete" title="删除">
          <Trash2 :size="14" />
        </button>
      </span>
    </div>
    </div>
    <!-- 底部固定：缩放把手 -->
    <div class="resize-handle" @mousedown.prevent="startResize" title="拖拽缩放" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { exclusiveConfirm } from '@/utils/exclusiveConfirm'
import { Trash2, Copy, Lock, Unlock, Pin, Eye, EyeOff, Grid2x2 } from 'lucide-vue-next'
import { useSceneStore, useSelectionStore } from '../stores/scene'
import { setHighlight } from '../three/ObjectManager'
import { cloneSceneObjectAt } from '../utils/cloneSceneObject'
import { getNonOverlappingPosition } from '../utils/spawnPosition'
import { applyAlignment, type AlignMode } from '../three/AlignmentTool'
import { baseDisplayName, buildObjectDisplayNameMap } from '../utils/objectDisplayNames'
import { storeToRefs } from 'pinia'
import SizePanel from './SizePanel.vue'
import { groupObjectIds, setInfoCardEnabledForObjectIds, setLockedForObjectIds, setStatusLightEnabledForObjectIds, setTopLabelVisibleForObjectIds, uniformColorForObjectIds, ungroupObjectIds } from '@/utils/batchObjectOps'

const props = defineProps<{
  id: string
  pos: { x: number; y: number }
  multiSelectIds?: string[]
  onHistoryPush?: () => void
}>()
const emit = defineEmits(['close', 'resize-start', 'resize-end', 'open-array'])

const sceneStore = useSceneStore()
const selectionStore = useSelectionStore()
const { selectedIds, bubbleMenuPinned: pinned, bubbleMenuFixedPos } = storeToRefs(selectionStore)

const isMultiSelect = computed(() => selectedIds.value.length > 1)

const bulkSameType = computed(() => {
  const ids = selectedIds.value
  if (ids.length < 2) return false
  const objs = ids
    .map((id) => sceneStore.activeObjects().find((o) => o.id === id))
    .filter((o): o is NonNullable<typeof o> => !!o)
  if (objs.length !== ids.length) return false
  const t0 = objs[0]!.type
  return objs.every((o) => o.type === t0)
})

const selectionHasGroup = computed(() =>
  selectedIds.value.some((id) => !!sceneStore.activeObjects().find((o) => o.id === id)?.groupId)
)

const selectedObjs = computed(() =>
  selectedIds.value
    .map((id) => sceneStore.activeObjects().find((o) => o.id === id))
    .filter((o): o is NonNullable<typeof o> => !!o)
)
const batchTopLabelVisible = computed(() =>
  selectedObjs.value.length > 0 && selectedObjs.value.every((o) => o.label?.visible === true)
)
const batchStatusLightVisible = computed(() =>
  selectedObjs.value.length > 0 && selectedObjs.value.every((o) => o.statusLight?.enabled === true)
)
const batchInfoCardVisible = computed(() =>
  selectedObjs.value.length > 0 && selectedObjs.value.every((o) => o.infoCard?.enabled === true)
)

const showBulkTransform = ref(false)
const batchColorHex = ref('#409EFF')
watch(
  () => [...selectedIds.value],
  () => {
    showBulkTransform.value = false
  }
)

const editingName = ref(false)
const nameInputRef = ref<HTMLInputElement | null>(null)
const nameVal = ref('')

const obj = computed(() => sceneStore.activeObjects().find(o => o.id === props.id))
const objDisplayName = computed(() => {
  const o = obj.value
  if (!o) return ''
  return buildObjectDisplayNameMap(sceneStore.activeObjects()).get(o.id) ?? baseDisplayName(o)
})
const positionDisplay = computed(() => {
  const p = obj.value?.position
  if (!p || !Array.isArray(p)) return null
  return { x: p[0].toFixed(2), y: p[1].toFixed(2), z: p[2].toFixed(2) }
})

const coordX = computed(() => obj.value?.position?.[0] ?? 0)
const coordY = computed(() => obj.value?.position?.[1] ?? 0)
const coordZ = computed(() => obj.value?.position?.[2] ?? 0)

function onCoordChange(axis: 'x' | 'y' | 'z', raw: string) {
  const o = obj.value
  if (!o) return
  const v = Number.parseFloat(raw)
  if (Number.isNaN(v)) return
  props.onHistoryPush?.()
  const p = o.position
  if (axis === 'x') sceneStore.updateObject(props.id, { position: [v, p[1], p[2]] })
  else if (axis === 'y') sceneStore.updateObject(props.id, { position: [p[0], v, p[2]] })
  else sceneStore.updateObject(props.id, { position: [p[0], p[1], v] })
}

function snapToFloor() {
  const o = obj.value
  if (!o) return
  props.onHistoryPush?.()
  const floorId = o.floorId ?? 'f1'
  const floorElevation = sceneStore.activeFloors().find((f) => f.id === floorId)?.elevation ?? 0
  sceneStore.updateObject(props.id, { position: [o.position[0], floorElevation, o.position[2]] })
}
const isLocked = computed(() => obj.value?.locked ?? false)
const isHidden = computed(() => obj.value?.hidden ?? false)
const topLabelVisible = computed(() => obj.value?.label?.visible === true)
const statusLightVisible = computed(() => obj.value?.statusLight?.enabled === true)
const infoCardVisible = computed(() => obj.value?.infoCard?.enabled === true)

// 面板可拖拽、可缩放
const DEFAULT_WIDTH = 260
const MULTI_WIDTH = 292
const DEFAULT_HEIGHT = 160
const SINGLE_BUBBLE_PLACEHOLDER_H = 148
const MULTI_CLAMP_H = 480
const MIN_WIDTH = 200
const MIN_HEIGHT = 120

const panelLeft = ref(0)
const panelTop = ref(0)
const panelWidth = ref(DEFAULT_WIDTH)
const panelHeight = ref(DEFAULT_HEIGHT)
/** 多选默认按内容撑开；用户拖过缩放把手后才使用固定高度 */
const multiUserResized = ref(false)

watch(isMultiSelect, (v) => {
  if (!v) return
  if (panelWidth.value <= DEFAULT_WIDTH) panelWidth.value = MULTI_WIDTH
}, { immediate: true })

const GAP = 12
const MARGIN = 8
const RIGHT_PANEL_WIDTH = 300
const RIGHT_PANEL_GAP = 10
/** 顶栏高度 48px；气泡不得压到其下，否则手柄被遮挡无法拖动 */
const TOP_BAR_HEIGHT = 48
const MIN_TOP = TOP_BAR_HEIGHT + MARGIN
const FIXED_TOP = 78

function clampPanelPosition(left: number, top: number, panelW: number, panelH: number) {
  return {
    left: Math.max(MARGIN, Math.min(window.innerWidth - panelW - MARGIN, left)),
    top: Math.max(MIN_TOP, Math.min(window.innerHeight - panelH - 60, top)),
  }
}

function currentPanelHeight(): number {
  if (!isMultiSelect.value) return SINGLE_BUBBLE_PLACEHOLDER_H
  if (multiUserResized.value) return panelHeight.value
  return Math.min(MULTI_CLAMP_H, window.innerHeight - MIN_TOP - 24)
}

function getDefaultFixedPosition(): { left: number; top: number } {
  const panelH = currentPanelHeight()
  const panelW = panelWidth.value
  const targetLeft = window.innerWidth - RIGHT_PANEL_WIDTH - RIGHT_PANEL_GAP - panelW - GAP
  return clampPanelPosition(targetLeft, FIXED_TOP, panelW, panelH)
}

function getInitialPosition(): { left: number; top: number } {
  const panelH = currentPanelHeight()
  const panelW = panelWidth.value
  const px = props.pos.x
  const py = props.pos.y

  const spaceAbove = py - MARGIN
  const spaceBelow = window.innerHeight - py - MARGIN
  const spaceLeft = px - MARGIN
  const spaceRight = window.innerWidth - px - MARGIN

  // 选择空间最大且能放下面板的方向；若都不足则选空间相对最大的
  const candidates: { placement: 'above' | 'below' | 'left' | 'right'; space: number }[] = [
    { placement: 'above', space: spaceAbove },
    { placement: 'below', space: spaceBelow },
    { placement: 'left', space: spaceLeft },
    { placement: 'right', space: spaceRight }
  ]
  candidates.sort((a, b) => b.space - a.space)
  const best = candidates[0]!

  let left = 0
  let top = 0

  switch (best.placement) {
    case 'above':
      left = px - panelW / 2
      top = py - GAP - panelH
      break
    case 'below':
      left = px - panelW / 2
      top = py + GAP
      break
    case 'left':
      left = px - GAP - panelW
      top = py - panelH / 2
      break
    case 'right':
      left = px + GAP
      top = py - panelH / 2
      break
  }

  return clampPanelPosition(left, top, panelW, panelH)
}

function togglePin() {
  const next = !pinned.value
  selectionStore.setBubbleMenuPinned(next)
  if (next) selectionStore.setBubbleMenuFixedPos(panelLeft.value, panelTop.value)
}

// 菜单显示时初始化位置（固定时所有对象共用一个固定位置；未固定则随选中对象移动）
watch(
  () =>
    [
      props.id,
      props.pos?.x,
      props.pos?.y,
      selectedIds.value.length,
      pinned.value,
    ] as const,
  () => {
    if (pinned.value) {
      if (bubbleMenuFixedPos.value) {
        const pos = clampPanelPosition(
          bubbleMenuFixedPos.value.left,
          bubbleMenuFixedPos.value.top,
          panelWidth.value,
          currentPanelHeight()
        )
        panelLeft.value = pos.left
        panelTop.value = pos.top
      } else {
        // 默认固定在右侧对象列表左侧，避免遮挡场景主体
        const pos = getDefaultFixedPosition()
        panelLeft.value = pos.left
        panelTop.value = pos.top
        selectionStore.setBubbleMenuFixedPos(pos.left, pos.top)
      }
      return
    }
    if (!props.pos || (!props.id && selectedIds.value.length <= 1)) return
    const { left, top } = getInitialPosition()
    panelLeft.value = left
    panelTop.value = top
  },
  { immediate: true }
)

// 若气泡已钻进顶栏下（历史固定位置 / 旧逻辑），立即拉回，避免手柄被遮挡后无法拖动
watch(
  panelTop,
  (top) => {
    if (top >= MIN_TOP) return
    panelTop.value = MIN_TOP
    if (pinned.value) selectionStore.setBubbleMenuFixedPos(panelLeft.value, MIN_TOP)
  },
  { immediate: true }
)

// 拖拽移动
let dragStart = { x: 0, y: 0, left: 0, top: 0 }

function startDrag(e: MouseEvent) {
  // 若已压在顶栏下，先拉回安全区，保证手柄可点到
  if (panelTop.value < MIN_TOP) {
    panelTop.value = MIN_TOP
  }
  dragStart = { x: e.clientX, y: e.clientY, left: panelLeft.value, top: panelTop.value }
  window.addEventListener('mousemove', onDragMove)
  window.addEventListener('mouseup', stopDrag)
  emit('resize-start')
}

function onDragMove(e: MouseEvent) {
  const dx = e.clientX - dragStart.x
  const dy = e.clientY - dragStart.y
  const panelH = currentPanelHeight()
  const pos = clampPanelPosition(
    dragStart.left + dx,
    dragStart.top + dy,
    panelWidth.value,
    panelH
  )
  panelLeft.value = pos.left
  panelTop.value = pos.top
}

function stopDrag() {
  window.removeEventListener('mousemove', onDragMove)
  window.removeEventListener('mouseup', stopDrag)
  if (pinned.value) selectionStore.setBubbleMenuFixedPos(panelLeft.value, panelTop.value)
  emit('resize-end')
}

// 缩放
let resizeStart = { x: 0, y: 0, w: 0, h: 0 }

function startResize(e: MouseEvent) {
  if (isMultiSelect.value && !multiUserResized.value) {
    panelHeight.value = Math.max(MIN_HEIGHT, Math.min(MULTI_CLAMP_H, currentPanelHeight()))
    multiUserResized.value = true
  }
  resizeStart = {
    x: e.clientX,
    y: e.clientY,
    w: panelWidth.value,
    h: panelHeight.value
  }
  window.addEventListener('mousemove', onResizeMove)
  window.addEventListener('mouseup', stopResize)
  emit('resize-start')
}

function onResizeMove(e: MouseEvent) {
  const dw = e.clientX - resizeStart.x
  const dh = e.clientY - resizeStart.y
  panelWidth.value = Math.max(MIN_WIDTH, resizeStart.w + dw)
  panelHeight.value = Math.max(MIN_HEIGHT, resizeStart.h + dh)
}

function stopResize() {
  window.removeEventListener('mousemove', onResizeMove)
  window.removeEventListener('mouseup', stopResize)
  emit('resize-end')
}

const menuStyle = computed(() => {
  const style: Record<string, string> = {
    left: `${panelLeft.value}px`,
    top: `${panelTop.value}px`,
    width: `${panelWidth.value}px`,
    maxHeight: `calc(100vh - ${MIN_TOP + 16}px)`,
  }
  if (multiUserResized.value) style.height = `${panelHeight.value}px`
  return style
})

/** 单选气泡：高度随内容，避免固定高度产生大块留白；宽度仍可拖拽缩放 */
const menuStyleSingle = computed(() => ({
  left: `${panelLeft.value}px`,
  top: `${panelTop.value}px`,
  width: `${panelWidth.value}px`,
}))


function startEditName() {
  const o = obj.value
  if (!o) return
  nameVal.value = o.name?.trim() || baseDisplayName(o)
  editingName.value = true
  nextTick(() => nameInputRef.value?.focus())
}

function saveName() {
  const o = obj.value
  if (!o) {
    editingName.value = false
    return
  }
  const next = nameVal.value.trim()
  const prev = o.name?.trim() ?? ''
  if (next !== prev) props.onHistoryPush?.()
  sceneStore.updateObject(props.id, { name: next })
  editingName.value = false
}

function doDelete() {
  props.onHistoryPush?.()
  setHighlight(props.id, false)
  sceneStore.removeObject(props.id)
  selectionStore.deselect()
  emit('close')
}

function doDuplicate() {
  const current = obj.value
  if (!current) return
  props.onHistoryPush?.()
  const offsetX = (current.params?.width ?? current.params?.size ?? 3) + 1
  const preferredX = current.position[0] + offsetX
  const preferredZ = current.position[2]
  const yElevation = current.position[1]
  const existing = sceneStore.activeObjects()
  const position = getNonOverlappingPosition(preferredX, preferredZ, current.type, current.params ?? {}, existing, yElevation)
    ?? [preferredX, yElevation, preferredZ]
  sceneStore.addObject(cloneSceneObjectAt(current, position))
}

function toggleLock() {
  props.onHistoryPush?.()
  sceneStore.updateObject(props.id, { locked: !isLocked.value })
}

function toggleHidden() {
  props.onHistoryPush?.()
  sceneStore.updateObject(props.id, { hidden: !isHidden.value })
}

function toggleTopLabel() {
  props.onHistoryPush?.()
  const o = obj.value
  if (!o) return
  const cur = o.label ?? { text: '', visible: false }
  sceneStore.updateObject(props.id, { label: { ...cur, visible: !topLabelVisible.value } })
}

function toggleStatusLight() {
  props.onHistoryPush?.()
  const o = obj.value
  if (!o) return
  const next = !statusLightVisible.value
  const cur = o.statusLight ?? { enabled: false, color: 'gray' as const, label: '' }
  sceneStore.updateObject(props.id, {
    statusLight: {
      ...cur,
      enabled: next,
      // 开启时必须带文字：空则默认「未知」，保证对象上显示完整状态标记（色点+文字）
      label: next ? cur.label || '未知' : cur.label,
    },
  })
}

function toggleInfoCard() {
  props.onHistoryPush?.()
  const o = obj.value
  if (!o) return
  const cur = o.infoCard ?? { enabled: false, fields: [] }
  sceneStore.updateObject(props.id, { infoCard: { ...cur, enabled: !infoCardVisible.value } })
}

// 多选操作
async function doDeleteAll() {
  const n = selectedIds.value.length
  if (n > 1) {
    const ok = await exclusiveConfirm(`确定删除选中的 ${n} 个对象？`, '删除确认')
    if (!ok) return
  }
  props.onHistoryPush?.()
  selectedIds.value.forEach(id => {
    setHighlight(id, false)
    sceneStore.removeObject(id)
  })
  selectionStore.deselect()
  emit('close')
}

function doAlign(mode: AlignMode) {
  props.onHistoryPush?.()
  const results = applyAlignment(selectedIds.value, mode, sceneStore.activeObjects())
  results.forEach(r => {
    if (r.position) sceneStore.updateObject(r.id, { position: r.position })
    if (r.params) sceneStore.updateObjectParams(r.id, r.params)
  })
}

function applyBatchUniformColor() {
  if (!selectedIds.value.length) return
  props.onHistoryPush?.()
  uniformColorForObjectIds(sceneStore, selectedIds.value, batchColorHex.value)
}

function doGroup() {
  if (selectedIds.value.length < 2) return
  props.onHistoryPush?.()
  groupObjectIds(sceneStore, selectedIds.value)
}

function doUngroup() {
  if (!selectedIds.value.length) return
  props.onHistoryPush?.()
  ungroupObjectIds(sceneStore, selectedIds.value)
}

function doBatchPrefixName() {
  const prefix = window.prompt('输入名称前缀（例如 A区-）', '')
  if (!prefix) return
  props.onHistoryPush?.()
  selectedIds.value.forEach((id, idx) => {
    const target = sceneStore.activeObjects().find(o => o.id === id)
    if (!target) return
    sceneStore.updateObject(id, { name: `${prefix}${idx + 1}` })
  })
}

function doBatchLock(locked: boolean) {
  props.onHistoryPush?.()
  setLockedForObjectIds(sceneStore, selectedIds.value, locked)
}

function doBatchTopLabel(visible: boolean) {
  if (!selectedIds.value.length) return
  props.onHistoryPush?.()
  setTopLabelVisibleForObjectIds(sceneStore, selectedIds.value, visible)
}

function doBatchStatusLight(enabled: boolean) {
  if (!selectedIds.value.length) return
  props.onHistoryPush?.()
  setStatusLightEnabledForObjectIds(sceneStore, selectedIds.value, enabled)
}

function doBatchInfoCard(enabled: boolean) {
  if (!selectedIds.value.length) return
  props.onHistoryPush?.()
  setInfoCardEnabledForObjectIds(sceneStore, selectedIds.value, enabled)
}

function doBatchInfoCardLine() {
  const key = window.prompt('信息卡字段名（例如 本班产量）', '')
  if (!key) return
  const value = window.prompt('信息卡默认值', '')
  if (value == null) return
  props.onHistoryPush?.()
  selectedIds.value.forEach(id => {
    const target = sceneStore.activeObjects().find(o => o.id === id)
    if (!target) return
    const fields = [...(target.infoCard?.fields ?? [])]
    const existed = fields.findIndex(f => f.key === key)
    if (existed >= 0) fields[existed] = { key, value }
    else fields.push({ key, value })
    sceneStore.updateObject(id, { infoCard: { enabled: true, fields: fields.slice(0, 5) } })
  })
}
</script>

<style scoped>
.bubble-menu {
  position: fixed;
  z-index: var(--z-sys-bubble);
  background: #1a2535;
  border: 1px solid #3a5070;
  border-radius: 10px;
  padding: 10px;
  padding-bottom: 16px; /* 为底部缩放把手留空 */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  pointer-events: all;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bubble-menu.multi-select {
  height: auto;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  gap: 8px;
  padding: 8px 8px 16px;
}

.bubble-menu.multi-select::-webkit-scrollbar { width: 6px; }
.bubble-menu.multi-select::-webkit-scrollbar-thumb {
  background: #3a4a60;
  border-radius: 3px;
}

.bubble-menu--single {
  height: auto;
  padding: 8px;
  padding-bottom: 12px;
}

.bubble-menu-head {
  flex-shrink: 0;
}

.bubble-menu-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.bubble-menu--single .bubble-title {
  min-height: 24px;
  margin-bottom: 4px;
}

.bubble-title {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin-bottom: 6px;
  gap: 6px;
}

.bubble-title--sticky {
  position: sticky;
  top: -8px;
  z-index: 2;
  margin: -8px -8px 0;
  padding: 8px 8px 6px;
  background: #1a2535;
  border-bottom: 1px solid #2a3a50;
}

.drag-handle {
  flex-shrink: 0;
  padding: 4px 6px;
  margin: -4px -2px -4px 0;
  color: #5a6a80;
  cursor: grab;
  user-select: none;
  font-size: 12px;
  line-height: 1;
}

.drag-handle:hover {
  color: #7a9acc;
}

.drag-handle:active {
  cursor: grabbing;
}

.pin-btn {
  flex: 0 0 auto;
  min-width: 28px;
  margin-left: auto;
}
.pin-btn.active {
  color: #4a9eff;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 50%, #3a5070 50%, #3a5070 55%, transparent 55%);
  border-radius: 0 0 10px 0;
}

.resize-handle:hover {
  background: linear-gradient(135deg, transparent 50%, #4a9eff 50%, #4a9eff 55%, transparent 55%);
}

.obj-name {
  color: #e0f0ff;
  font-size: 13px;
  font-weight: 600;
  cursor: text;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name-input {
  flex: 1;
  background: #243550;
  border: 1px solid #4a9eff;
  color: #e0f0ff;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  outline: none;
}

.bubble-divider {
  height: 1px;
  background: #2a3a50;
  margin: 6px 0;
}

.bubble-menu--single .bubble-divider {
  margin: 4px 0;
}

.bubble-coord-row {
  font-size: 12px;
  color: #6b7c9e;
  margin: 4px 0 6px;
  white-space: nowrap;
}

.bubble-menu--single .bubble-coord-row--edit {
  margin: 2px 0 4px;
}

.bubble-actions {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 28px;
  padding: 5px 3px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
  color: #ccc;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover { background: #2e4060; border-color: #4a9eff; }
.action-btn.active { background: #2a4a70; border-color: #4a9eff; }
.action-btn.danger:hover { background: #501520; border-color: #ff4455; }

/* 参数面板：高度随内容展开，由外层面板缩放控制可见区域 */
.params-panel {
  overflow: visible;
}

/* 外观面板 */
.sub-panel {
  margin-top: 8px;
  border-top: 1px solid #2a3a50;
  padding-top: 8px;
}

.imported-sub {
  padding: 0;
  border-top: none;
  margin-top: 0;
}

.appearance-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.appearance-row label {
  font-size: 11px;
  color: #8899aa;
  width: 30px;
}

.appearance-row input[type="color"] {
  width: 40px;
  height: 24px;
  border: 1px solid #3a4a60;
  border-radius: 3px;
  padding: 1px;
  background: transparent;
  cursor: pointer;
}

.material-btns {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
}

.mat-btn {
  padding: 3px 6px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #8899aa;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.mat-btn.active, .mat-btn:hover {
  background: #1e4a8a;
  border-color: #4a9eff;
  color: #ddf;
}

/* 信息卡 */
.info-card-header {
  font-size: 11px;
  color: #8899aa;
  margin-bottom: 6px;
}

.info-card-row {
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  align-items: center;
}

.card-input {
  flex: 1;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  color: #c0d8f0;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 11px;
  outline: none;
}

.card-input:focus { border-color: #4a9eff; }

.remove-btn {
  background: transparent;
  border: none;
  color: #556677;
  cursor: pointer;
  font-size: 11px;
  padding: 0 2px;
}

.remove-btn:hover { color: #ff4455; }

.add-field-btn {
  width: 100%;
  padding: 4px;
  background: transparent;
  border: 1px dashed #3a4a60;
  border-radius: 4px;
  color: #556677;
  cursor: pointer;
  font-size: 11px;
  margin-top: 4px;
}

.add-field-btn:hover { border-color: #4a9eff; color: #4a9eff; }

.panel-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  margin-top: 8px;
}

.panel-actions button {
  padding: 3px 12px;
  background: #243040;
  border: 1px solid #3a4a60;
  color: #aac;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
}

.confirm-btn {
  background: #1e4a8a !important;
  border-color: #4a9eff !important;
  color: #ddf !important;
}

/* 状态灯 */
.status-light-row {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.status-color-btn {
  flex: 1;
  padding: 4px 2px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 5px;
  color: #aac;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}

.status-color-btn.active {
  background: #1e4a8a;
  border-color: #4a9eff;
}
.status-dot {
  vertical-align: -0.2em;
  margin-right: 4px;
  display: inline-block;
  flex-shrink: 0;
}

.status-text-input {
  flex: 1;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  color: #c0d8f0;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 11px;
  outline: none;
}

.bubble-coord-row--edit {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7c9e;
}

.coord-label {
  color: #5a6a80;
  font-size: 11px;
  flex-shrink: 0;
}

.coord-input {
  width: 44px;
  min-width: 0;
  flex: 1 1 0;
  max-width: 52px;
  padding: 3px 4px;
  box-sizing: border-box;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #e0f0ff;
  font-size: 12px;
}

.coord-y-hint {
  margin-left: auto;
  font-size: 11px;
  color: #4a5a70;
}

.bubble-follow-floor-row {
  margin: -2px 0 6px;
  padding: 0 2px;
}

/* 单选气泡：贴回楼面 + 显示名称/状态 三个开关，等宽铺满一行不换行 */
.bubble-single-toggle-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.bubble-single-toggle-row .action-btn {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  padding: 5px 3px;
}

/* 多选：显示名称/状态标签 左右对半、各自左对齐 */
.bubble-label-toggle-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  column-gap: 8px;
}

.bubble-follow-floor-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8ba0bc;
  user-select: none;
  cursor: pointer;
}

.bubble-actions--grouped {
  align-items: center;
}

.action-group {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  align-items: center;
}

.action-group--icons {
  width: 100%;
  justify-content: space-between;
  flex-wrap: nowrap;
}

.action-group--icons .action-btn {
  flex: 1;
  min-width: 0;
  flex-direction: row;
  gap: 0;
  padding: 6px 4px;
  font-size: 12px;
  justify-content: center;
}

.bubble-menu--single .action-group--icons .action-btn {
  padding: 4px 3px;
}

.action-btn-label {
  font-size: 9px;
  color: #8899aa;
  line-height: 1;
  white-space: nowrap;
}

.action-btn.danger .action-btn-label {
  color: #cc6677;
}

.action-sep {
  color: #3a4a60;
  padding: 0 4px;
  user-select: none;
}

.action-btn-text {
  flex: 0 0 auto;
  min-width: 44px;
  padding: 5px 8px;
  font-size: 12px;
}

.bubble-actions--color {
  align-items: center;
  margin-top: 6px;
}

.multi-color-label {
  font-size: 11px;
  color: #9bb4d0;
  min-width: 48px;
}

.multi-color-input {
  width: 34px;
  height: 24px;
  padding: 0;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
}

.mini-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.mini-tab {
  flex: 1;
  padding: 5px 8px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 6px;
  color: #8899aa;
  cursor: pointer;
  font-size: 12px;
}

.mini-tab.active {
  background: #1e4a7a;
  border-color: #4a9eff;
  color: #e0f0ff;
}

.data-combined-panel {
  padding-top: 4px;
}

.multi-row-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5a6a80;
  margin: 6px 0 4px;
}

.multi-row-label--danger {
  color: #a44;
}

.multi-action-row {
  flex-wrap: wrap;
}

.multi-action-row .action-btn {
  flex: 1 1 auto;
  min-width: 72px;
  font-size: 11px;
}

/* 对齐工具 */
.align-panel {
  margin-top: 8px;
  border-top: 1px solid #2a3a50;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.align-panel--compact {
  margin-top: 0;
  border-top: none;
  padding-top: 0;
}

.batch-ops {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
}

.bubble-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bubble-section-title {
  margin: 0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6a7e98;
}

.bubble-section .action-btn.danger {
  width: 100%;
  gap: 6px;
  font-size: 12px;
  padding: 6px 8px;
}

.align-btn.active {
  background: #1e4a8a;
  border-color: #4a9eff;
  color: #ddf;
}

.align-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.align-row {
  display: flex;
  gap: 4px;
}

.align-btn {
  flex: 1;
  padding: 4px 2px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #aac;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}

.align-btn.wide {
  flex: 2;
}

.align-btn:hover {
  background: #2e4060;
  border-color: #4a9eff;
  color: #ddf;
}


.signal-bindings-panel {
  font-size: 12px;
}

.signal-hint {
  font-size: 11px;
  color: #75889e;
  line-height: 1.45;
  margin-bottom: 10px;
}

.signal-input-wide {
  flex: 1;
  min-width: 0;
}

.signal-row-tight {
  margin-bottom: 6px;
}

.signal-row-tight label {
  min-width: 112px;
  flex-shrink: 0;
}

.signal-field-label {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signal-subhead {
  font-size: 11px;
  font-weight: 600;
  color: #8aa0b8;
  margin: 12px 0 6px;
}

.color-rule-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.signal-select {
  padding: 3px 4px;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #c0d8f0;
  font-size: 11px;
}

.signal-threshold {
  width: 72px;
}

.signal-map-row {
  flex-direction: column;
  align-items: stretch;
}

.signal-map-row label {
  min-width: auto;
  margin-bottom: 4px;
}

.signal-textarea {
  width: 100%;
  box-sizing: border-box;
  background: #1e2e42;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #c0d8f0;
  font-size: 11px;
  font-family: ui-monospace, monospace;
  padding: 6px;
  resize: vertical;
}
</style>
